import type { Project } from "@/interfaces/project";
import formatTime from "@/utils/formatTime";
import clockIcon from "@/assets/icons/clock.svg";
import Card from "@/components/ui/Card";
import Badge from "@/components/ui/Badge";
import Icon from "@/components/ui/Icon";

interface ProjectCardProps {
  project: Project & { username?: string };
  link?: string;
  newTab?: boolean;
  variant?: "default" | "homepage";
}

export default function ProjectCard({
  project,
  link = `${project.demo_link}`,
  newTab = false,
}: ProjectCardProps) {
  return (
    <a
      href={link}
      {...(newTab && { target: "_blank", rel: "noopener noreferrer" })}
      className="group block h-full"
    >
      <Card
        interactive
        className={
          project.high_quality ? "border-goldenrod shadow-raised" : undefined
        }
        media={
          project.screenshot ? (
            <img
              src={project.screenshot}
              alt={project.title ?? "Project screenshot"}
              className="h-32 w-full object-cover"
            />
          ) : (
            <div className="bg-surface-alt h-32 w-full" />
          )
        }
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display text-heading text-ink min-w-0 leading-tight font-bold wrap-anywhere">
            {project.title}
          </h2>
          <div className="text-ink-muted flex shrink-0 items-center gap-1.5">
            <Icon src={clockIcon} label="Time logged" className="h-4 w-4" />
            <span className="font-display text-body font-bold">
              {formatTime(project.reported_seconds)}
            </span>
          </div>
        </div>

        {project.username && (
          <p className="text-caption text-ink-soft mt-0.5">
            by {project.username}
          </p>
        )}

        {project.desc && (
          <p className="text-body text-ink-muted mt-2 line-clamp-3 wrap-break-word">
            {project.desc}
          </p>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
          {project.high_quality && <Badge tone="brand">High quality</Badge>}
          {project.unread_notification_count > 0 &&
            link !== project.demo_link && (
              <Badge tone="danger">
                {project.unread_notification_count} unread
              </Badge>
            )}
        </div>
      </Card>
    </a>
  );
}
